import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import { Pagination } from '../../components/UI/Pagination';
import { Spinner } from '../../components/UI/Spinner';
import { EmptyState } from '../../components/UI/EmptyState';
import {
  Users,
  Search,
  Trash2,
  Mail,
} from 'lucide-react';

export const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { success, error: toastError } = useToast();

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.customers({ search, page });
      if (res && res.data) {
        setCustomers(res.data.data || []);
        setPagination({
          currentPage: res.data.current_page,
          lastPage: res.data.last_page,
          total: res.data.total,
          from: res.data.from,
          to: res.data.to,
        });
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar clientes globales.');
    } finally {
      setLoading(false);
    }
  }, [search, page, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCustomers();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchCustomers]);

  const handleDelete = async () => {
    if (!customerToDelete) return;
    setDeleting(true);
    try {
      await adminService.deleteCustomer(customerToDelete.id);
      success('Cliente eliminado del sistema.');
      setDeleteConfirmOpen(false);
      setCustomerToDelete(null);
      fetchCustomers();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar cliente.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
          <Users className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
          Clientes Globales del Sistema
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
          Directorio global de todos los clientes vinculados a usuarios registrados
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="w-full sm:w-80">
            <Input
              placeholder="Buscar por cliente o teléfono..."
              icon={Search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="py-1.5"
            />
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando clientes globales..." className="py-12" />
          ) : customers.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No hay clientes registrados"
              description="No se encontraron registros de clientes."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4">Cliente</th>
                    <th className="py-3 px-4">Pertenece al Usuario</th>
                    <th className="py-3 px-4">Teléfonos</th>
                    <th className="py-3 px-4">Dirección</th>
                    <th className="py-3 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {customers.map((c) => (
                    <tr
                      key={c.id}
                      className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-900 dark:text-slate-100">
                          {c.full_name}
                        </div>
                        {c.email && (
                          <div className="text-[11px] text-slate-400 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {c.email}
                          </div>
                        )}
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-800 dark:text-slate-200">
                          {c.user?.name || '—'}
                        </div>
                        <div className="text-[11px] text-slate-400">{c.user?.email}</div>
                      </td>

                      <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300">
                        {c.cellphone || c.telephone || '—'}
                      </td>

                      <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300">
                        {c.address1 || '—'}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Trash2}
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => {
                            setCustomerToDelete(c);
                            setDeleteConfirmOpen(true);
                          }}
                          title="Eliminar cliente"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
            total={pagination.total}
            from={pagination.from}
            to={pagination.to}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="¿Eliminar cliente del sistema?"
        message={`¿Estás seguro de que deseas eliminar al cliente "${customerToDelete?.full_name}"?`}
        confirmText="Eliminar"
        isLoading={deleting}
      />
    </div>
  );
};
